import React, { useMemo, useState } from "react";
import { ArrowUp, ArrowDown } from "lucide-react";

/**
 * Accessible, sortable stat table.
 * columns: [{ key, label, better: "high"|"low"|null, fmt, sortValue, sticky }]
 * rows: array of objects. `fmt(row)` renders the cell; `sortValue(row)` returns the numeric sort key.
 */
export default function StatTable({ columns, rows, initialSort, initialDir = "desc", caption, testid, sortable = true }) {
  const [sortKey, setSortKey] = useState(initialSort || null);
  const [dir, setDir] = useState(initialDir);

  const col = columns.find((c) => c.key === sortKey);

  const sorted = useMemo(() => {
    if (!sortKey || !col) return rows;
    const val = col.sortValue || ((r) => r[col.key]);
    const arr = [...rows];
    arr.sort((a, b) => {
      const va = val(a), vb = val(b);
      const aNull = va == null || Number.isNaN(va);
      const bNull = vb == null || Number.isNaN(vb);
      if (aNull && bNull) return 0;
      if (aNull) return 1; // nulls always sink to bottom
      if (bNull) return -1;
      return dir === "asc" ? va - vb : vb - va;
    });
    return arr;
  }, [rows, sortKey, dir, col]);

  const onSort = (c) => {
    if (!sortable || c.better === null) return;
    if (sortKey === c.key) {
      setDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(c.key);
      setDir(c.better === "low" ? "asc" : "desc");
    }
  };

  return (
    <div className="overflow-x-auto -mx-1 px-1" data-testid={testid}>
      <table className="w-full text-sm border-collapse min-w-max">
        {caption && <caption className="sr-only">{caption}</caption>}
        <thead>
          <tr className="border-b-2 border-[#0A192F]">
            {columns.map((c) => {
              const active = sortKey === c.key;
              const canSort = sortable && c.better !== null;
              return (
                <th
                  key={c.key}
                  scope="col"
                  aria-sort={active ? (dir === "asc" ? "ascending" : "descending") : "none"}
                  className={`px-2.5 py-2 font-data text-[11px] uppercase tracking-wider text-slate-500 ${c.sticky ? "sticky left-0 bg-white text-left z-10" : "text-right"} ${canSort ? "cursor-pointer select-none hover:text-[#0A192F]" : ""}`}
                >
                  {canSort ? (
                    <button
                      type="button"
                      onClick={() => onSort(c)}
                      data-testid={`${testid}-sort-${c.key}`}
                      aria-label={`Sort by ${c.label}`}
                      className={`inline-flex items-center gap-1 focus-visible:ring-2 focus-visible:ring-[#E63946] focus-visible:outline-none ${c.sticky ? "" : "flex-row-reverse"} ${active ? "text-[#E63946]" : ""}`}
                    >
                      <span>{c.label}</span>
                      {active && (dir === "asc" ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />)}
                    </button>
                  ) : (
                    c.label
                  )}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {sorted.map((r, i) => (
            <tr key={r._key || i} data-testid={`${testid}-row-${r._key ?? i}`} className="border-b border-slate-100 hover:bg-slate-50">
              {columns.map((c) => (
                <td
                  key={c.key}
                  className={`px-2.5 py-2 font-data ${c.sticky ? "sticky left-0 bg-white text-left font-semibold text-[#0A192F] z-10" : "text-right tabular-nums text-slate-700"}`}
                >
                  {c.fmt ? c.fmt(r) : r[c.key]}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
